import styled from 'styled-components';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useMemo, useState } from 'react';
import Pagination from '../../../components/table/pagination';
import { IndeterminateCheckbox } from '../../../components/table/checkbox';
import Select from 'react-select';
import {
  fetchApproveMember,
  fetchDeleteMember,
} from '../../../api/admin.member';
import { ConfirmModal } from '../../../components/modal/confirm.modal';
import { AlertModal } from '../../../components/modal/alert.modal';
import { useHistory } from 'react-router-dom';

const tableSort = [
  { value: '10', label: '10개씩 보기' },
  { value: '20', label: '20개씩 보기' },
  { value: '50', label: '50개씩 보기' },
];

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface MemberDtoRx {
  userid: number; // 아이디 (숫자 타입)
  passid: string; // 로그인 아이디 (문자열 타입)
  memberType: string; // 회원 구분 (문자열 타입)
  name: string; // 회사명 (문자열 타입)
  address: string; // 지역 구분 (문자열 타입)
  charge: string; // 담당자 이름 (문자열 타입)
  position: string; // 담당자 직책 (문자열 타입)
  phone: string; // 휴대전화 (문자열 타입)
  createdAt: string; // 가입일 (문자열, 날짜 형식으로 예상)
  state: string; // 계정 상태 (문자열 타입)
}
// AdminMemberTableComp 컴포넌트
interface AdminMemberTableCompProps {
  data: MemberDtoRx[];
  setMemberList: React.Dispatch<React.SetStateAction<MemberDtoRx[] | null>>; // 데이터 갱신 함수 (삭제 후 업데이트)
}

export const AdminMemberTableComp = ({
  data,
  setMemberList,
}: AdminMemberTableCompProps) => {
  const { register, handleSubmit } = useForm();

  const history = useHistory();

  const onSubmit = (data: any) => {};

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // 모달
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [actionType, setActionType] = useState<'delete' | 'approve' | null>(
    null,
  ); // 동작 타입을 관리

  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleConfirmClose = () => {
    setConfirmModal(false);
    setActionType(null); // actionType 초기화
  };

  const columns = useMemo<ColumnDef<MemberDtoRx>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          // 전체 선택이 필요한 경우
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomePageRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
                onClick: (e) => e.stopPropagation(),
              }}
            />
          </div>
        ),
        size: 45,
      },
      {
        header: '번호',
        id: 'number', // 넘버링을 위한 id
        cell: ({ row }) => row.index + 1, // 각 행의 인덱스에 1을 더해 넘버링
        size: 76,
      },
      {
        header: '아이디',
        accessorKey: 'passid',
        cell: (info) => info.getValue(),
        size: 160,
      },
      {
        header: '회원 구분',
        accessorKey: 'memberType',
        cell: (info) => info.getValue(),
        size: 120,
      },
      {
        header: '회사명',
        accessorKey: 'name',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 120,
      },
      {
        header: '지역구분',
        accessorKey: 'address',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 160,
      },
      {
        header: '담당자 이름',
        accessorKey: 'charge',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 140,
      },
      {
        header: '담당자 직책',
        accessorKey: 'position',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 130,
      },
      {
        header: '휴대전화',
        accessorKey: 'phone',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 130,
      },
      {
        header: '가입일',
        accessorKey: 'createdAt',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        size: 110,
      },
      {
        header: '계정 상태',
        accessorKey: 'state',
        cell: (info) =>
          info.getValue() === 'Assigned' ? (
            <span className="state assigned">대기</span>
          ) : (
            <span className="state activation">승인</span>
          ),
        size: 110,
      },
    ],
    [register],
  );
  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  const selectedUserIds = useMemo(() => {
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key]) // 선택된 행만 필터링
      .map((key) => {
        const row = table.getRowModel().rows[parseInt(key)];
        return row.original.userid; // 선택된 행의 original 데이터에서 userid 가져오기
      });
  }, [rowSelection, data]);

  const handleDelete = async () => {
    if (selectedUserIds.length === 0) {
      setModalMessage('삭제할 회원을 선택하세요.');
      setIsModalOpen(true);
      return;
    }

    //삭제 API 호출
    try {
      const response = await fetchDeleteMember({ userId: selectedUserIds });
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
      } else {
        setModalMessage(response.message);
        setIsModalOpen(true);
      }

      // 삭제된 항목을 로컬 상태에서 제거
      const updatedData = data.filter(
        (member) => !selectedUserIds.includes(member.userid),
      );
      setMemberList(updatedData); // 상위 컴포넌트의 상태 업데이트
      setRowSelection({}); // 선택된 행 초기화
    } catch (error) {
      console.error('회원 삭제 실패:', error);
      setModalMessage('회원 삭제에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  const handleApprove = async () => {
    if (selectedUserIds.length === 0) {
      setModalMessage('승인할 회원을 선택하세요.');
      setIsModalOpen(true);
      return;
    }

    try {
      await fetchApproveMember({ userId: selectedUserIds });
      setModalMessage('회원 승인이 완료되었습니다.');

      const updatedData = data.map((member) =>
        selectedUserIds.includes(member.userid)
          ? { ...member, state: '승인' }
          : member,
      );
      setMemberList(updatedData);
      setRowSelection({});
    } catch (error) {
      console.error('회원 승인 실패:', error);
      setModalMessage('회원 승인 중 오류가 발생했습니다.');
    } finally {
      setIsModalOpen(true);
    }
  };

  const handleConfirmAction = () => {
    setConfirmModal(false);
    if (actionType === 'delete') {
      handleDelete(); // 삭제 동작
    } else if (actionType === 'approve') {
      handleApprove(); // 승인 동작
    }
    setActionType(null); // actionType 초기화
  };

  const handlePageSizeChange = (selectedOption: any) => {
    const newSize = selectedOption ? parseInt(selectedOption.value, 10) : 10; // 선택되지 않은 경우 기본값으로 10을 사용
    table.setPageSize(newSize);
  };

  const selectedValue = tableSort.find(
    (option) => option.value === String(table.getState().pagination.pageSize),
  );

  const handleRowClick = (rowData: MemberDtoRx) => {
    history.push({
      pathname: `/admin/member/list/detail/${rowData.userid}`,
      state: { memberType: rowData.memberType },
    });
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="table-head-wrap">
          <div className="left">
            <span className="table-total-row">
              전체 {table.getRowCount()}건
            </span>
          </div>
          <div className="right">
            <Select
              className="search-select"
              classNamePrefix="react-select"
              options={tableSort}
              onChange={handlePageSizeChange}
              components={{
                IndicatorSeparator: () => null,
              }}
              value={
                tableSort.find(
                  (option) =>
                    option.value ===
                    String(table.getState().pagination.pageSize),
                ) || tableSort[0]
              }
            />
            <div className="btn-box">
              <button
                className="delete"
                onClick={() => {
                  setConfirmModal(true);
                  setActionType('delete'); // 삭제 동작 설정
                }}
              >
                선택 삭제
              </button>
              <button
                className="approval"
                onClick={() => {
                  setConfirmModal(true);
                  setActionType('approve'); // 승인 동작 설정
                }}
              >
                선택 승인
              </button>
            </div>
          </div>
        </div>
        <table>
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row.original)} // 행을 클릭할 때 handleRowClick 호출
                style={{ cursor: 'pointer' }} // 마우스 커서를 클릭 모양으로 변경
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    //className={cell.column.columnDef.meta?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={pagination.pageIndex}
          pages={table.getPageCount()}
          onPageChange={(page) => {
            table.setPageIndex(page);
          }}
        />
      </form>

      {confirmModal && (
        <ConfirmModal
          handleConfirmClose={handleConfirmClose}
          confirmText={
            actionType === 'delete'
              ? '선택한 회원을 삭제하시겠습니까?'
              : '선택한 회원을 승인하시겠습니까?'
          }
          onClick={handleConfirmAction} // 확인 시 handleConfirmAction 호출
        />
      )}
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

// style
const Container = styled.section`
  max-width: 140rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 11.4rem;
  margin: 0 auto;
  form {
    table {
      margin-top: 2.4rem;
      width: 100%;
      font-size: 1.3rem;
      line-height: 1;
      letter-spacing: -5%;

      input[type='checkbox'] {
        width: 2rem;
        height: 2rem;
        border-color: ${(props) => props.theme.btnBlueColor};
      }

      thead {
        border-top: 2px solid ${(props) => props.theme.btnBlueColor};
        th {
          background-color: ${(props) => props.theme.textWhiteColor};
        }
      }
      th,
      td {
        padding: 1rem 1rem;
        border-bottom: 1px solid #f0f0f0;
        text-align: center;
        white-space: nowrap;
        .datail-btn {
          text-decoration: underline;
          cursor: pointer;
        }
        vertical-align: middle;
      }
      tbody {
        td {
          padding: 0.8rem 0.23rem;
          cursor: pointer;
        }
      }
      td.sm-padding {
        padding: 0.9rem 0;
      }
      .left-align {
        text-align: left;
      }
      .px-1 {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .table-input {
        input {
          border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
          font-size: 1.3rem;
          height: 2.8rem;
          border-radius: 0.8rem;
          max-width: 6rem;
        }
      }
      tfoot {
        background-color: ${(props) => props.theme.costBgColor};
        font-weight: 600;
        tr {
          td:first-child {
            text-align: left;
            padding-left: 2rem;
          }
        }
      }
      .state {
        width: 4.2rem;
        height: 2.4rem;
        font-size: 1.2rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.4rem;
        margin: 0 auto;
        &.assigned {
          color: #727272;
          background-color: #e0e0e0;
        }
        &.activation {
          color: ${(props) => props.theme.textWhiteColor};
          background-color: #62cc9f;
        }
      }
    }
    .table-head-wrap {
      margin-top: 3.2rem;
      margin-bottom: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .left {
        .table-total-row {
          padding: 0 1px;
          font-size: 1.2rem;
          font-weight: 600;
          color: ${(props) => props.theme.gray500};
        }
      }
      .right {
        display: flex;
        gap: 1.2rem;
        align-items: center;
        .search-select {
          width: 18rem;
          font-size: 1.3rem;
          .react-select__placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
          }
          .react-select__control {
            border-color: ${(props) => props.theme.searchBoxBorderedColor};
            border-radius: 0.8rem;
            .react-select__value-container {
              padding: 0.8rem 1.6rem;
            }
          }
        }
        .btn-box {
          display: flex;
          gap: 0.8rem;
          button {
            width: 8rem;
            font-size: 1.4rem;
            font-weight: 500;
            padding: 1.1rem 0;
            border-radius: 0.4rem;
            outline: none;
            border: 1px solid;
            background-color: ${(props) => props.theme.textWhiteColor};
            &.delete {
              color: #81858e;
              border-color: #dbdee3;
            }
            &.approval {
              color: ${(props) => props.theme.btnBlueColor};
              border-color: ${(props) => props.theme.btnBlueColor};
            }
          }
        }
      }
    }
  }
`;
